/**
 * Post-login pre-warm for triage / details / light tx caches.
 *
 * PR 232 kept the public dashboard catalog warm across OTP. Officers still
 * waited on cold triage/trace after re-login. This module hits cache-backed
 * address endpoints for the active Workbench session + recent addresses so
 * the first Inspect / Trace after OTP is already warm.
 */
import { addressService } from '../services/api';
import { normalizeChain } from './canvasSync';
import { detectChain } from './chainDetect';
import { getDashboardCached } from './dashboardCache';

export type WarmTarget = {
    chain: string;
    address: string;
    source: string;
};

type RecentlyViewedItem = {
    resource_type: string;
    resource_id: string;
    resource_data?: {
        address?: string;
        chain?: string;
        label?: string;
    };
};

const EVM = new Set([
    'ETH', 'BNB', 'MATIC', 'ARB', 'OP', 'BASE', 'AVAX', 'FTM', 'LINEA', 'BLAST', 'SCROLL', 'ZKSYNC',
]);

function normalizeAddress(address: string, chain: string): string {
    const trimmed = (address || '').trim();
    if (!trimmed) return '';
    if (EVM.has(normalizeChain(chain))) return trimmed.toLowerCase();
    return trimmed;
}

function targetKey(chain: string, address: string): string {
    return `${normalizeChain(chain)}:${normalizeAddress(address, chain)}`;
}

function pushTarget(
    out: WarmTarget[],
    seen: Set<string>,
    chain: string | null | undefined,
    address: string | null | undefined,
    source: string,
    limit: number,
): void {
    if (out.length >= limit) return;
    const addr = (address || '').trim();
    if (!addr) return;
    const resolvedChain = normalizeChain(chain || detectChain(addr));
    const key = targetKey(resolvedChain, addr);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ chain: resolvedChain, address: normalizeAddress(addr, resolvedChain), source });
}

function readZustandPersistState(key: string): Record<string, unknown> | null {
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { state?: Record<string, unknown> };
        return parsed?.state && typeof parsed.state === 'object' ? parsed.state : null;
    } catch {
        return null;
    }
}

/** Collect warm targets from Workbench session / graph + recently viewed. */
export function collectWarmTargets(limit = 5): WarmTarget[] {
    const out: WarmTarget[] = [];
    const seen = new Set<string>();

    const session = readZustandPersistState('workbench-session');
    if (session) {
        pushTarget(
            out,
            seen,
            typeof session.sessionChain === 'string' ? session.sessionChain : null,
            typeof session.sessionAddress === 'string' ? session.sessionAddress : null,
            'workbench-session',
            limit,
        );
    }

    const graph = readZustandPersistState('workbench-graph');
    const elements = Array.isArray(graph?.elements) ? (graph!.elements as Array<{ data?: Record<string, unknown> }>) : [];
    for (const el of elements) {
        if (out.length >= limit) break;
        const data = el?.data;
        if (!data || data.source || data.type !== 'address') continue;
        const id = typeof data.id === 'string' ? data.id : '';
        if (!id || id.includes(':')) continue; // skip placeholders like ghost:…
        pushTarget(
            out,
            seen,
            typeof data.chain === 'string' ? data.chain : null,
            id,
            'workbench-graph',
            limit,
        );
    }

    const recent = getDashboardCached<RecentlyViewedItem[]>('recentlyViewed') || [];
    for (const item of recent) {
        if (out.length >= limit) break;
        if (item?.resource_type !== 'address') continue;
        const addr = item.resource_data?.address || item.resource_id;
        pushTarget(out, seen, item.resource_data?.chain, addr, 'recently-viewed', limit);
    }

    return out;
}

async function warmOne(target: WarmTarget, includeTx: boolean): Promise<void> {
    const jobs: Promise<unknown>[] = [
        addressService.getDetails(target.chain, target.address),
        addressService.getTriage(target.chain, target.address, 1, { fast: true }),
    ];
    if (includeTx) {
        jobs.push(
            addressService.getTransactions(target.chain, target.address, 30, 0, undefined, undefined, undefined, undefined, {
                includeAcceleration: true,
            }),
        );
    }
    await Promise.allSettled(jobs);
}

/**
 * Fire-and-forget after JWT is written. Does not throw; safe to void.
 * Primary (first) target also warms a light transaction page for trace UX.
 */
export async function warmInvestigationCaches(opts?: { limit?: number }): Promise<void> {
    try {
        if (typeof localStorage === 'undefined' || !localStorage.getItem('token')) return;
        const targets = collectWarmTargets(opts?.limit ?? 5);
        if (targets.length === 0) return;
        await Promise.allSettled(
            targets.map((t, i) => warmOne(t, i === 0)),
        );
    } catch {
        /* never block login */
    }
}
